import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  HeroSection,
  ParentingChallengesSection,
  HowItWorksSection,
  CoreProductGrid,
  PhilosophySection,
  FeaturedSystemSection,
  LeadMagnetSection,
} from '../components/home';
import { api } from '../services/api';
import adminApi from '../services/adminApi';

/**
 * Homepage — Phase 4 DB-driven.
 *
 * Content for every section is fetched from `GET /api/homepage`
 * (the currently-published homepage doc). Sections still render with
 * their existing hardcoded copy as a safe fallback if the API fails.
 *
 * Preview mode: `?preview=1` fetches the current draft via
 * `GET /api/admin/homepage/draft` (requires admin auth in localStorage).
 * This lets Marketing preview unpublished changes without going live.
 */
const Home = () => {
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [content, setContent] = useState(null);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let payload = null;
      if (isPreview) {
        try {
          const savedCreds = localStorage.getItem('admin_creds');
          if (savedCreds) {
            const creds = JSON.parse(savedCreds);
            const token = btoa(`${creds.username}:${creds.password}`);
            payload = await adminApi.getHomepageDraft({ Authorization: `Basic ${token}` });
          }
        } catch {
          // fall back to published
        }
      }
      if (!payload) {
        payload = await api.getHomepage();
      }
      if (!cancelled) {
        setContent(payload?.sections || null);
        setIsPreviewLoaded(isPreview && !!payload?.status && payload.status === 'draft');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isPreview]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="home-page">
      {isPreviewLoaded && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-sm font-semibold text-center py-2" data-testid="homepage-preview-banner">
          Preview mode — showing DRAFT content. Not yet visible to customers.
        </div>
      )}
      <HeroSection content={content?.hero} />
      <ParentingChallengesSection content={content?.parenting_challenges} />
      <HowItWorksSection content={content?.how_it_works} />
      <CoreProductGrid content={content?.featured_collections} />
      <PhilosophySection content={content?.philosophy} />
      <FeaturedSystemSection content={content?.featured_product} />
      <LeadMagnetSection content={content?.lead_magnet} newsletter={content?.newsletter} />
    </div>
  );
};

export default Home;
